/**
 * Сторис на главной: модель экрана поверх витринного DTO
 * (`@dorixona/api/stories`). Тексты держим на обоих языках — язык выбирает
 * экран при отрисовке, так смена языка не требует нового запроса.
 */
import { mediaUrl } from '@dorixona/api'
import { STORY_DURATION_DEFAULT, type PublicStoryDto } from '@dorixona/api/stories'
import type { Language } from '@dorixona/i18n'

export type Texts = Record<Language, string>

export interface StorySlide {
  id: number
  image: string
  /** Сколько слайд стоит на экране, мс. */
  durationMs: number
  /** Подпись кнопки; пусто на обоих языках — кнопки нет. */
  button: Texts
  /** Путь приложения (`/catalog`) или `https://…`. */
  link: string
}

export interface Story {
  id: number
  /** Ключ просмотра: id и время правки — изменённую сторис покупатель увидит снова. */
  seenKey: string
  title: Texts
  /** Картинка в кружке: обложка или, если её нет, первый слайд. */
  cover: string
  slides: StorySlide[]
}

/** Текст на языке интерфейса; пусто — на другом языке. */
export function pick(texts: Texts, language: Language): string {
  const own = texts[language].trim()
  if (own) return own
  return texts[language === 'ru' ? 'uz' : 'ru'].trim()
}

/** Витринная сторис → модель экрана. Сторис без слайдов показать нечем — её нет. */
export function toStory(dto: PublicStoryDto, apiBaseUrl: string): Story | null {
  const slides = dto.slides.map<StorySlide>((slide) => ({
    id: slide.id,
    image: mediaUrl(slide.image_url, apiBaseUrl),
    durationMs: (slide.duration_sec > 0 ? slide.duration_sec : STORY_DURATION_DEFAULT) * 1000,
    button: { ru: slide.button_ru, uz: slide.button_uz },
    link: slide.link,
  }))
  const first = slides[0]
  if (!first) return null
  return {
    id: dto.id,
    seenKey: `${dto.id}:${dto.updated_at}`,
    title: { ru: dto.title_ru, uz: dto.title_uz },
    cover: dto.cover_url ? mediaUrl(dto.cover_url, apiBaseUrl) : first.image,
    slides,
  }
}

/** Порядок — серверный (`sort`): непросмотренные вперёд не переставляем. */
export function toStories(dtos: PublicStoryDto[], apiBaseUrl: string): Story[] {
  return dtos.map((dto) => toStory(dto, apiBaseUrl)).filter((story) => story !== null)
}

export type LinkTarget = { kind: 'internal'; path: string } | { kind: 'external'; url: string }

/**
 * Куда ведёт кнопка. Путь приложения начинается с одного `/` (`//host` — это
 * чужой сайт без схемы), внешняя ссылка — только `https://`. Остальное кнопку
 * не показывает: вести покупателя в неизвестность нельзя.
 */
export function linkTarget(link: string): LinkTarget | null {
  if (link.startsWith('/') && !link.startsWith('//')) return { kind: 'internal', path: link }
  if (link.startsWith('https://')) return { kind: 'external', url: link }
  return null
}

export const SEEN_STORAGE_KEY = 'dorixona.stories.seen.v2'

type ReadStorage = Pick<Storage, 'getItem'>
type WriteStorage = Pick<Storage, 'setItem'>

/** Просмотренные сторис — в браузере, ключами `id:updated_at`. */
export function readSeen(storage?: ReadStorage): string[] {
  try {
    const raw: unknown = JSON.parse((storage ?? localStorage).getItem(SEEN_STORAGE_KEY) ?? '[]')
    return Array.isArray(raw) ? raw.filter((key): key is string => typeof key === 'string') : []
  } catch {
    return []
  }
}

export function writeSeen(keys: string[], storage?: WriteStorage): void {
  try {
    ;(storage ?? localStorage).setItem(SEEN_STORAGE_KEY, JSON.stringify(keys))
  } catch {
    // Хранилище закрыто (приватный режим) — кольца просто не запомнятся.
  }
}

/**
 * Отметить сторис просмотренной. Ключи сторис, которых на витрине уже нет,
 * выбрасываем: иначе список в браузере только рос бы.
 */
export function markSeen(seen: string[], story: Story, stories: Story[]): string[] {
  if (seen.includes(story.seenKey)) return seen
  const live = new Set(stories.map((item) => item.seenKey))
  return [...seen.filter((key) => live.has(key)), story.seenKey]
}
